import { TextInput } from "@narmi/design_system";
import React from "react";
import { Control, Controller, FieldValues, Path } from "react-hook-form";
import { countryCodeToFlag } from "byzantine/src/dbbl/utils/internationalPhoneNumber";
import utils from "byzantine/src/utils";

interface CurrencyAmountInputProps<T extends FieldValues> {
  control?: Control<T, unknown>;
  value?: string;
  name?: Path<T>;
  label: string;
  currency: string;
  disabled?: boolean;
  showEndContent?: boolean;
}
const onChange = (
  fieldOnChange: (any: any) => void,
  currency: string,
  event: React.ChangeEvent<HTMLInputElement>
) => {
  if (!currency) {
    const raw = event.target.value.replace(/[^\d.]/g, "");
    const parts = raw.split(".");
    const sanitized =
      parts.length > 1 ? `${parts[0]}.${parts.slice(1).join("").slice(0, 3)}` : parts[0];
    fieldOnChange(sanitized);
    return;
  }
  const currencyFractionDigits =
    new Intl.NumberFormat(undefined, { style: "currency", currency }).resolvedOptions()
      .maximumFractionDigits || 0;
  const target = event.target; // let synthetic event go back to the pool, but save our target
  const prevCursorPos = target.selectionStart || 0;
  // re-interpret our digits, inserting the decimal point relative to the currency
  const rawValueArray = [...target.value.replace(/[^\d]/g, "")];
  if (rawValueArray.length === 0) {
    fieldOnChange("");
    return;
  }
  if (currencyFractionDigits !== 0) {
    rawValueArray.splice(rawValueArray.length - currencyFractionDigits, 0, ".");
  }
  const rawValue = rawValueArray.join("");
  // update our value
  fieldOnChange(rawValue);
  // restore cursor position
  requestAnimationFrame(() => {
    target.selectionStart = prevCursorPos;
    target.selectionEnd = prevCursorPos;
  });
};

function CurrencyAmountInput<T extends FieldValues>({
  control,
  value,
  name,
  label,
  currency,
  showEndContent = true,
  ...otherProps
}: CurrencyAmountInputProps<T>) {
  const endContent = React.useMemo(
    () =>
      showEndContent && currency ? (
        <span className={"currency-amount-input end-content"}>
          <div>{countryCodeToFlag(currency.substring(0, 2))}</div>
          <div>{currency}</div>
        </span>
      ) : null,
    [currency, showEndContent]
  );
  if (!control || !name) {
    return (
      <TextInput
        label={label}
        value={utils.currencyFormattedValue(value || "", currency)}
        endContent={endContent}
        {...otherProps}
      />
    );
  }
  return (
    <Controller
      name={name}
      control={control}
      render={(renderProps) => (
        <TextInput
          label={label}
          onChange={onChange.bind(null, renderProps.field.onChange, currency)}
          value={utils.currencyFormattedValue(renderProps.field.value, currency)}
          error={renderProps.fieldState.error?.message}
          endContent={endContent}
          {...otherProps}
        />
      )}
    />
  );
}

export default CurrencyAmountInput;
